import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  YouTube as YouTubeIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  ChevronRight as ChevronRightIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { surface } from '../theme';
import ChannelService from '../services/ChannelService';
import AiStoriesChannelService from '../services/AiStoriesChannelService';
import AuthService from '../services/AuthService';
import ChannelBadge from '../components/ChannelBadge';
import { pickRandomChannelColor } from '../utils/channelColors';
import MobileListCard from '../components/responsive/MobileListCard';
import ChannelYouTubeDialog from '../components/ChannelYouTubeDialog';
import ChannelYouTubeService from '../services/ChannelYouTubeService';
import VoiceOverConfigService from '../services/VoiceOverConfigService';
import VideoFootageService from '../services/VideoFootageService';
import { getPromptSystems } from '../services/StoryScriptService';
import ChannelAutomationDialog, { AUTOMATION_STATE_SEVERITY } from '../components/ChannelAutomationDialog';

const VIDEOS_PER_DAY_OPTIONS = Array.from({ length: 12 }, (_, index) => index + 1);
const AUTOMATION_STATE_COLORS = {
  success: '#28724f',
  warning: '#956419',
  error: '#b3261e',
  info: '#727786',
};

const TAB_AI_STORIES = 0;
const TAB_SLEEP = 1;

const TAB_SLUG = {
  [TAB_SLEEP]: 'sleep',
  [TAB_AI_STORIES]: 'ai-stories',
};

const SLUG_TO_TAB = {
  sleep: TAB_SLEEP,
  'ai-stories': TAB_AI_STORIES,
};

function ChannelsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { tab: tabSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedChannelId = searchParams.get('channelId');
  const requestedYouTubeChannelId = searchParams.get('youtube');
  const activeTab = SLUG_TO_TAB[tabSlug];
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [formName, setFormName] = useState('');
  const [formProfileNumber, setFormProfileNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [formVoiceId, setFormVoiceId] = useState('');
  const [formFootageId, setFormFootageId] = useState('');
  const [footageFolders, setFootageFolders] = useState([]);
  const [voiceConfigs, setVoiceConfigs] = useState([]);
  const [youtubeChannel, setYoutubeChannel] = useState(null);
  const [automationChannel, setAutomationChannel] = useState(null);
  const [formAutomated, setFormAutomated] = useState(false);
  const [formVideosPerDay, setFormVideosPerDay] = useState(1);
  const [formNiche, setFormNiche] = useState('');
  const [promptSystems, setPromptSystems] = useState([]);

  const [deleteChannel, setDeleteChannel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);
  const [mobileMenuChannel, setMobileMenuChannel] = useState(null);

  const isAiStories = activeTab === TAB_AI_STORIES;

  const loadChannels = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const list = isAiStories
        ? await AiStoriesChannelService.list()
        : await ChannelService.list();
      const channelsWithConnections = await Promise.all(
        (Array.isArray(list) ? list : []).map(async (channel) => {
          try {
            const youtube = await ChannelYouTubeService.get(
              isAiStories ? 'ai-stories' : 'sleep', channel.id,
            );
            return { ...channel, youtube };
          } catch {
            return { ...channel, youtube: null };
          }
        }),
      );
      setChannels(channelsWithConnections);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || 'Failed to load channels',
      );
    } finally {
      setLoading(false);
    }
  }, [isAiStories]);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  useEffect(() => {
    setDialogOpen(false);
    setEditingChannel(null);
    setDeleteChannel(null);
    setYoutubeChannel(null);
    setAutomationChannel(null);
  }, [activeTab]);

  useEffect(() => {
    if (!isAiStories) return undefined;
    let active = true;
    getPromptSystems()
      .then(data => { if (active) setPromptSystems(data.items || []); })
      .catch(() => { if (active) setError('Could not load niches.'); });
    return () => { active = false; };
  }, [isAiStories]);

  // New automation defaults to Bikers, the first niche with an idea backlog.
  const defaultNiche = () =>
    promptSystems.find(system => system.niche === 'bikers')?.niche || promptSystems[0]?.niche || '';

  useEffect(() => {
    let active = true;
    setVoiceConfigs([]);
    VoiceOverConfigService.list(isAiStories ? 'ai-stories' : 'sleep-videos')
      .then(list => { if (active) setVoiceConfigs(list); })
      .catch(() => { if (active) setError('Could not load voice configurations.'); });
    return () => { active = false; };
  }, [isAiStories]);

  useEffect(() => {
    let active = true;
    VideoFootageService.listFolders({ all: true, tag: isAiStories ? 'ai-story' : 'sleep-channel' })
      .then(data => { if (active) setFootageFolders(data.items || []); })
      .catch(() => { if (active) setError('Could not load footage folders.'); });
    return () => { active = false; };
  }, [isAiStories, dialogOpen]);

  const handleOpenCreate = () => {
    setFormAutomated(false);
    setFormVideosPerDay(1);
    setFormNiche(defaultNiche());
    setFormVoiceId('');
    setFormFootageId('');
    setEditingChannel(null);
    setFormName('');
    setFormProfileNumber('');
    setSaveError(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (channel) => {
    setFormAutomated(Boolean(channel.automation?.enabled));
    setFormVideosPerDay(channel.automation?.videosPerDay || 1);
    setFormNiche(channel.automation?.niche || defaultNiche());
    setFormVoiceId(channel.voiceOverConfigId || '');
    setFormFootageId(channel.footageFolderId || '');
    setEditingChannel(channel);
    setFormName(channel.name || '');
    setFormProfileNumber(channel.profileNumber != null ? String(channel.profileNumber) : '');
    setSaveError(null);
    setDialogOpen(true);
  };

  useEffect(() => {
    if (loading || !requestedChannelId) return;
    const channel = channels.find(item => item.id === requestedChannelId);
    if (channel) handleOpenEdit(channel);
  }, [loading, channels, requestedChannelId]);

  useEffect(() => {
    if (loading || !requestedYouTubeChannelId) return;
    const channel = channels.find(item => item.id === requestedYouTubeChannelId);
    if (channel) setYoutubeChannel(channel);
  }, [loading, channels, requestedYouTubeChannelId]);

  const handleCloseDialog = () => {
    if (!saving) {
      setSearchParams({}, { replace: true });
      setDialogOpen(false);
      setEditingChannel(null);
      setFormName('');
      setFormProfileNumber('');
      setSaveError(null);
    }
  };

  const handleSave = async () => {
    const name = formName?.trim();
    if (!name) {
      setSaveError('Channel name is required');
      return;
    }
    const num = parseInt(formProfileNumber, 10);
    const minProfileNumber = isAiStories ? 1 : 0;
    if (Number.isNaN(num) || num < minProfileNumber) {
      setSaveError(
        isAiStories
          ? 'Profile number must be an integer greater than 0'
          : 'Profile number must be an integer greater than or equal to 0',
      );
      return;
    }
    try {
      setSaving(true);
      setSaveError(null);
      if (isAiStories) {
        const automation = {
          enabled: formAutomated,
          videosPerDay: formVideosPerDay,
          niche: formNiche,
        };
        if (editingChannel) {
          await AiStoriesChannelService.update(editingChannel.id, {
            automation,
            voiceOverConfigId: formVoiceId || null,
            footageFolderId: formFootageId || null,
            name,
            profileNumber: num,
            color: (editingChannel.color || '').trim() || '',
          });
        } else {
          await AiStoriesChannelService.create({
            automation,
            voiceOverConfigId: formVoiceId || null,
            footageFolderId: formFootageId || null,
            name,
            profileNumber: num,
            color: pickRandomChannelColor(),
          });
        }
      } else if (editingChannel) {
        await ChannelService.update(editingChannel.id, {
          voiceOverConfigId: formVoiceId || null,
          footageFolderId: formFootageId || null,
          name,
          profileNumber: num,
          color: (editingChannel.color || '').trim() || '',
        });
      } else {
        await ChannelService.create({
          voiceOverConfigId: formVoiceId || null,
          footageFolderId: formFootageId || null,
          name,
          profileNumber: num,
          color: pickRandomChannelColor(),
        });
      }
      setSearchParams({}, { replace: true });
      setDialogOpen(false);
      setEditingChannel(null);
      setFormName('');
      setFormProfileNumber('');
      loadChannels();
    } catch (err) {
      setSaveError(
        err.response?.data?.error || err.message || 'Failed to save channel',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (channel, e) => {
    e.stopPropagation();
    setDeleteChannel(channel);
  };

  const handleDeleteClose = () => {
    if (!deleting) setDeleteChannel(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteChannel) return;
    try {
      setDeleting(true);
      if (isAiStories) {
        await AiStoriesChannelService.delete(deleteChannel.id);
      } else {
        await ChannelService.delete(deleteChannel.id);
      }
      setDeleteChannel(null);
      loadChannels();
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || 'Failed to delete channel',
      );
    } finally {
      setDeleting(false);
    }
  };
  const openMobileMenu = (event, channel) => {
    event.stopPropagation();
    setMobileMenuAnchorEl(event.currentTarget);
    setMobileMenuChannel(channel);
  };
  const closeMobileMenu = () => {
    setMobileMenuAnchorEl(null);
    setMobileMenuChannel(null);
  };

  const renderYouTubeConnection = (channel) => {
    const connection = channel.youtube;
    const needsReconnect = connection?.needsReconnect;
    const connected = connection?.connected && !needsReconnect;
    const hasChannel = connection?.connected || needsReconnect;
    const title = hasChannel
      ? connection.youtubeChannelTitle || connection.youtubeChannelId
      : connection ? 'Not connected' : 'Connection unavailable';
    const actionLabel = needsReconnect
      ? 'Reconnect'
      : connected ? 'Manage' : connection ? 'Connect' : 'Review';
    const status = needsReconnect
      ? 'Reconnect to resume uploads'
      : connected ? 'Connected' : connection ? 'Link your YouTube channel' : 'Open settings to check';
    const statusColor = needsReconnect ? '#956419' : connected ? '#28724f' : '#727786';

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '36px minmax(0, 1fr) auto',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          maxWidth: 420,
          minHeight: 48,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 36,
            height: 36,
            borderRadius: '10px',
            color: hasChannel ? '#db303b' : '#727786',
            bgcolor: hasChannel ? alpha('#db303b', 0.07) : alpha('#727786', 0.07),
          }}
        >
          <YouTubeIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            title={title}
            sx={{ fontWeight: 600, color: '#202433', lineHeight: 1.5, overflowWrap: 'anywhere' }}
          >
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, color: statusColor }}>
            {connected && <CheckCircleIcon sx={{ fontSize: 13, flexShrink: 0 }} />}
            {needsReconnect && <ErrorOutlineIcon sx={{ fontSize: 13, flexShrink: 0 }} />}
            <Typography variant="caption" sx={{ lineHeight: 1.5 }}>
              {status}
            </Typography>
          </Box>
          {connected && connection.uploadBlockedReason && (
            <Tooltip title={connection.uploadBlockedReason}>
              <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 600 }}>
                {connection.longUploadsStatus === 'unknown'
                  ? 'Feature eligibility unverified'
                  : 'Intermediate features required'}
              </Typography>
            </Tooltip>
          )}
        </Box>
        <Button
          size="small"
          variant="text"
          onClick={() => setYoutubeChannel(channel)}
          aria-label={`${actionLabel} YouTube connection for ${channel.name}`}
          endIcon={connected ? <ChevronRightIcon /> : undefined}
          sx={{
            minWidth: 0,
            minHeight: { xs: 44, md: 36 },
            px: 1.25,
            borderRadius: '8px',
            fontSize: 13,
            fontWeight: 600,
            color: 'primary.dark',
            whiteSpace: 'nowrap',
            bgcolor: connected ? 'transparent' : alpha(theme.palette.primary.main, 0.08),
            boxShadow: 'none',
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.14), boxShadow: 'none' },
            '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
            '& .MuiButton-endIcon': { ml: 0.25 },
          }}
        >
          {actionLabel}
        </Button>
      </Box>
    );
  };

  const renderAutomation = (channel) => {
    const automation = channel.automation;
    if (!automation?.enabled) {
      return <Typography variant="body2" color="text.secondary">Manual</Typography>;
    }
    const severity = AUTOMATION_STATE_SEVERITY[automation.state] || 'info';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Chip
            size="small"
            color="primary"
            label={`Automated · ${automation.videosPerDay}/day`}
            sx={{ fontWeight: 600 }}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
            {automation.backlogCount ?? 0} idea{automation.backlogCount === 1 ? '' : 's'} in backlog
          </Typography>
          {automation.message && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: AUTOMATION_STATE_COLORS[severity],
                fontWeight: severity === 'success' ? 400 : 600,
              }}
            >
              {automation.message}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          startIcon={<ScheduleIcon />}
          onClick={() => setAutomationChannel(channel)}
          aria-label={`Open automation schedule for ${channel.name}`}
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          Schedule
        </Button>
      </Box>
    );
  };

  if (!AuthService.hasRole(['admin', 'superadmin'])) {
    return <Navigate to="/" replace />;
  }

  if (activeTab === undefined) {
    return <Navigate to="/channels/ai-stories" replace />;
  }

  const dialogTitleEdit = isAiStories ? 'Edit AI Stories channel' : 'Edit Sleep channel';
  const dialogTitleNew = isAiStories ? 'New AI Stories channel' : 'New Sleep channel';
  const emptyMessage = isAiStories
    ? 'No AI Stories channels yet. Click "New channel" to create one.'
    : 'No Sleep channels yet. Click "New channel" to create one.';

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: 'white',
          mb: 2,
          textShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      >
        Channels
      </Typography>

      <Paper
        sx={{
          borderRadius: 2,
          background: surface.glass,
          boxShadow: 2,
          mb: 2,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => navigate(`/channels/${TAB_SLUG[v]}`)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            pt: 1,
          }}
        >
          <Tab label="AI Stories channels" id="channels-tab-ai-stories" />
          <Tab label="Sleep channels" id="channels-tab-sleep" />
        </Tabs>
        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 2,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              New channel
            </Button>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : channels.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3 }}>
              {emptyMessage}
            </Typography>
          ) : isMobile ? (
            <Box>
              {channels.map((channel) => (
                <MobileListCard
                  key={channel.id}
                  title={channel.name}
                  subtitle={`Profile #${channel.profileNumber != null ? channel.profileNumber : '-'}`}
                  meta={channel.createdAt ? new Date(channel.createdAt).toLocaleDateString() : '—'}
                  primaryAction={
                    <Button size='small' onClick={() => handleOpenEdit(channel)} sx={{ minWidth: 0, px: 1 }}>
                      Edit
                    </Button>
                  }
                  secondaryAction={
                    <IconButton size='small' onClick={(e) => openMobileMenu(e, channel)}>
                      <MoreVertIcon fontSize='small' />
                    </IconButton>
                  }
                >
                  <ChannelBadge channel={channel} />
                  <Typography variant="body2">Footage: {channel.footageFolder?.name || 'Not configured'}</Typography>
                  {isAiStories && <Box sx={{ mt: 1 }}>{renderAutomation(channel)}</Box>}
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      YouTube channel
                    </Typography>
                    {renderYouTubeConnection(channel)}
                  </Box>
                </MobileListCard>
              ))}
              <Menu anchorEl={mobileMenuAnchorEl} open={Boolean(mobileMenuAnchorEl)} onClose={closeMobileMenu}>
                <MenuItem
                  onClick={() => {
                    if (mobileMenuChannel) handleOpenEdit(mobileMenuChannel);
                    closeMobileMenu();
                  }}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  sx={{ color: 'error.main' }}
                  onClick={(e) => {
                    if (mobileMenuChannel) handleDeleteClick(mobileMenuChannel, e);
                    closeMobileMenu();
                  }}
                >
                  Delete
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>YouTube channel</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Profile #</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Footage folder</TableCell>
                    {isAiStories && <TableCell sx={{ fontWeight: 700 }}>Publishing</TableCell>}
                    <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {channels.map((channel) => (
                    <TableRow key={channel.id} hover>
                      <TableCell>
                        <ChannelBadge channel={channel} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 320, width: '38%', py: 2.25 }}>
                        {renderYouTubeConnection(channel)}
                      </TableCell>
                      <TableCell>
                        {channel.profileNumber != null ? channel.profileNumber : '-'}
                      </TableCell>
                      <TableCell>
                        {channel.footageFolder?.name || 'Not configured'}
                      </TableCell>
                      {isAiStories && (
                        <TableCell sx={{ minWidth: 240 }}>{renderAutomation(channel)}</TableCell>
                      )}
                      <TableCell>
                        {new Date(channel.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          aria-label="Edit channel"
                          onClick={(e) => handleOpenEdit(channel)}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          aria-label="Delete channel"
                          onClick={(e) => handleDeleteClick(channel, e)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingChannel ? dialogTitleEdit : dialogTitleNew}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Channel name"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            error={!!saveError}
            helperText={saveError}
            margin="normal"
            disabled={saving}
          />
          <TextField
            fullWidth
            type="number"
            inputProps={{ min: isAiStories ? 1 : 0, step: 1 }}
            label="Profile number"
            value={formProfileNumber}
            onChange={(e) => setFormProfileNumber(e.target.value)}
            margin="normal"
            disabled={saving}
            helperText={
              isAiStories
                ? 'Must be unique within this channel type and greater than 0'
                : 'Must be unique within this channel type and at least 0'
            }
          />
          <TextField fullWidth select label="Footage folder" value={formFootageId}
            onChange={e => setFormFootageId(e.target.value)} margin="normal" disabled={saving}
            helperText="Each folder can belong to only one channel. Required before creating videos.">
            <MenuItem value="">Not configured</MenuItem>
            {footageFolders.filter(folder => !folder.assignedChannelId || folder.assignedChannelId === editingChannel?.id)
              .map(folder => <MenuItem key={folder.id} value={folder.id}>{folder.name}</MenuItem>)}
          </TextField>
          <TextField fullWidth select label="Default voice" value={formVoiceId}
            onChange={e => setFormVoiceId(e.target.value)} margin="normal" disabled={saving}
            helperText="Used when a video does not have its own voice configuration.">
            <MenuItem value="">No channel default</MenuItem>
            {voiceConfigs.map(config => <MenuItem key={config.id} value={config.id}>{config.name}</MenuItem>)}
          </TextField>
          {isAiStories && (
            <Box sx={{ mt: 2.5, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Publishing</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={formAutomated ? 'automated' : 'manual'}
                onChange={(_, value) => { if (value) setFormAutomated(value === 'automated'); }}
                disabled={saving}
                aria-label="Publishing mode"
              >
                <ToggleButton value="manual" sx={{ px: 2 }}>Manual</ToggleButton>
                <ToggleButton value="automated" sx={{ px: 2 }}>Automated</ToggleButton>
              </ToggleButtonGroup>
              {formAutomated ? (
                <>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                    <TextField select fullWidth label="Videos per day" value={formVideosPerDay}
                      onChange={e => setFormVideosPerDay(Number(e.target.value))} margin="normal" disabled={saving}>
                      {VIDEOS_PER_DAY_OPTIONS.map(count => <MenuItem key={count} value={count}>{count}</MenuItem>)}
                    </TextField>
                    <TextField select fullWidth label="Idea backlog niche" value={formNiche}
                      onChange={e => setFormNiche(e.target.value)} margin="normal" disabled={saving}>
                      {promptSystems.map(system => (
                        <MenuItem key={system.niche} value={system.niche}>{system.label || system.niche}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Typography variant="caption" color="text.secondary" component="p">
                    Every hour, new videos are made from the oldest ideas in this niche&apos;s backlog and
                    uploaded as scheduled YouTube videos at random times spread between 9 AM and 9 PM
                    Eastern. Requires a YouTube connection, a footage folder, and a default voice.
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                  Videos are created by hand on the AI Stories page.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formName?.trim() || !(parseInt(formProfileNumber, 10) >= (isAiStories ? 1 : 0))
              || (isAiStories && formAutomated && !formNiche)}
          >
            {saving ? 'Saving...' : editingChannel ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {youtubeChannel && <ChannelYouTubeDialog key={`${activeTab}-${youtubeChannel.id}`} channel={youtubeChannel}
        kind={isAiStories ? 'ai-stories' : 'sleep'} onClose={() => {
          if (requestedYouTubeChannelId) setSearchParams({}, { replace: true });
          setYoutubeChannel(null);
          loadChannels();
        }} />}

      {automationChannel && (
        <ChannelAutomationDialog
          key={automationChannel.id}
          channel={automationChannel}
          onClose={() => {
            setAutomationChannel(null);
            loadChannels();
          }}
        />
      )}

      <Dialog
        open={!!deleteChannel}
        onClose={handleDeleteClose}
        maxWidth="sm"
      >
        <DialogTitle>Delete channel</DialogTitle>
        <DialogContent>
          {deleteChannel && (
            <Typography>
              {isAiStories ? (
                <>
                  Delete this AI Stories channel? Scripts that use it will have their
                  channel cleared. This cannot be undone.
                  {deleteChannel.name && (
                    <> &quot;{deleteChannel.name}&quot; will be removed.</>
                  )}
                </>
              ) : (
                <>
                  Delete this Sleep channel? This cannot be undone.
                  {deleteChannel.name && (
                    <> &quot;{deleteChannel.name}&quot; will be removed.</>
                  )}
                </>
              )}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={
              deleting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ChannelsPage;
